/**
 * The moves somebody has made and not yet taken back, oldest first.
 *
 * Lifted out of the Leads board's state service, where it was an array and
 * three getters, so that the roll-off board can carry the same offer without
 * a second array and three more getters that would drift from the first.
 *
 * What it knows is the stack: what is on it, how many, which is next. What it
 * deliberately does NOT know is what a step is or how it is undone - a lead's
 * undo is a compensating stage move with a note, a hire's is the same call
 * against a different table, and both of those belong to the page. This is
 * the memory, not the action.
 *
 * NOT PERSISTED, on purpose. Across a real reload there is no way to know the
 * board still looks like the stack remembers, and an Undo that might act on a
 * stale memory is worse than one that starts empty. A page that wants the
 * stack to survive a view flip keeps the journal on a service that survives
 * it, which is where the Leads board keeps its own.
 */
export class UndoJournal<TStep> {
  private readonly steps: TStep[] = [];

  /** Records a step that can later be taken back. */
  record(step: TStep): void {
    this.steps.push(step);
  }

  /** The step Undo would act on next, without taking it. */
  get last(): TStep | undefined {
    return this.steps[this.steps.length - 1];
  }

  get count(): number {
    return this.steps.length;
  }

  get canUndo(): boolean {
    return this.steps.length > 0;
  }

  /**
   * Takes the newest step off the stack. Called AFTER the compensating action
   * succeeded, or when the step can no longer be acted on - never before,
   * because a step popped ahead of a failed undo is a right to change your
   * mind that was quietly thrown away.
   */
  retract(): TStep | undefined {
    return this.steps.pop();
  }

  /** Every step, oldest first, for anything that wants to read the history. */
  get all(): readonly TStep[] {
    return this.steps;
  }

  clear(): void {
    this.steps.length = 0;
  }
}
